import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { css } from "goober";
import { Dropdown } from "clutch/src/Dropdown/Dropdown.js";

import type { PaymentProviderType } from "@/feature-wallet/models/payment-method-provider.mjs";
import { PaymentTypeIcon } from "@/feature-wallet/Shared.jsx";
import { getPaymentMethodTranslation } from "@/feature-wallet/utils/payment-method-translation.mjs";
import { usePaymentMethods } from "@/feature-wallet/utils/payment-methods.mjs";
import AddCard from "@/inline-assets/add-card.svg";
import { classNames } from "@/util/class-names.mjs";

const NEW_METHOD = "__new-pay-method__";

export interface AdditionalDropdownPaymentMethod {
  label: Translation | string;
  Icon: React.ReactNode;
}

export function PaymentMethodDropdown({
  name,
  required,
  readOnly,
  disabled,

  allowedPaymentProviders,

  selectedPaymentMethodId,
  onSelectedPaymentMethod,
  onAddPaymentMethod,
}: {
  name?: string;
  required?: boolean;
  readOnly?: boolean;
  disabled?: boolean;

  allowedPaymentProviders?: PaymentProviderType | PaymentProviderType[];

  selectedPaymentMethodId: string;
  onSelectedPaymentMethod: (id: string) => void;
  onAddPaymentMethod?: () => void;
}) {
  const paymentMethods = usePaymentMethods();

  const onSelectPaymentMethod = useCallback(
    (paymentMethodId: string) => {
      if (paymentMethodId === NEW_METHOD) {
        onAddPaymentMethod?.();
        return;
      }

      onSelectedPaymentMethod(paymentMethodId);
    },
    [onAddPaymentMethod, onSelectedPaymentMethod],
  );

  const { t } = useTranslation();

  return (
    <Dropdown
      name={name}
      required={required}
      readOnly={readOnly}
      disabled={disabled}
      items={[
        ...paymentMethods.map((paymentMethod) => ({
          label: t(...getPaymentMethodTranslation(paymentMethod)),
          value: paymentMethod.id,
          icon: <PaymentTypeIcon {...paymentMethod} />,
          disabled:
            allowedPaymentProviders &&
            (typeof allowedPaymentProviders === "string"
              ? allowedPaymentProviders !== paymentMethod.providerType
              : !allowedPaymentProviders.includes(paymentMethod.providerType)),
        })),
        ...(onAddPaymentMethod && [
          {
            label: t("common:wallet.newPaymentMethod", "New Payment Method"),
            value: NEW_METHOD,
            icon: <AddCard />,
          },
        ]),
      ]}
      placeholder={t(
        "common:wallet.addSelectPaymentMethod",
        "Add/Select a payment method",
      )}
      value={selectedPaymentMethodId}
      onChange={onSelectPaymentMethod}
      textClassName="type-subtitle--semi"
      {...classNames(css`
        --border-size: 0;
      `)}
    />
  );
}
